import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandL, BrandT } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { getRarityColor, getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Auction } from 'components/transfers/Auction';
import { InstantBuy } from 'components/transfers/InstantBuy';
import { Transfer } from 'components/transfers/Transfer';

const Border = styled(Horizontal)`
  --rarity-color: var(--c-rgb-limited);

  @container ${laptopAndAbove} {
    background: linear-gradient(
      180deg,
      var(--c-nd-150) 0%,
      var(--c-nd-150) 0%,
      rgba(var(--rarity-color), 0.4) 100%
    );
    padding: 1px;
    border-radius: var(--double-unit);
  }
`;
const Root = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-black);
  width: 100%;
  justify-content: space-between;
  @container ${laptopAndAbove} {
    flex-direction: row;
    align-items: stretch;
    padding: var(--double-unit);
    border-radius: var(--double-unit);
  }
`;
const TitleBlock = styled(Vertical)`
  gap: var(--unit);
  width: 100%;
  padding-bottom: var(--triple-unit);
  @media ${laptopAndAbove} {
    align-items: stretch;
    justify-content: center;
    padding-bottom: 0;
    padding-right: var(--double-unit);
  }
`;
const Block = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  width: 100%;
  padding: var(--double-unit) 0;
  justify-content: center;

  @container ${laptopAndAbove} {
    justify-content: flex-start;
    padding: 0 var(--double-unit);
    &:last-child {
      padding-right: 0;
    }
  }
  &:not(:last-child) {
    border-bottom: 1px solid var(--c-nd-150);
    @container ${laptopAndAbove} {
      border-bottom: none;
      border-right: 1px solid var(--c-nd-150);
    }
  }
`;
const LeftContent = styled(BrandL)`
  text-align: left;
`;
type Props = {
  rarity: Rarity;
  sport: Sport;
};

export const MarketRoutesBlock = ({ rarity, sport }: Props) => {
  const {
    flags: { useNewMarketRoutes = false },
  } = useFeatureFlags();
  const auctionMarketUrl = generateSportPath(ANY_SPORT_MARKET_SHOP_AUCTIONS, {
    sport,
  });
  const instantBuyUrl = generateSportPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, {
    sport,
  });
  const secondaryMarketUrl = generateSportPath(
    ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
    {
      sport,
    }
  );

  const color = getRarityColor(rarity);

  if (!useNewMarketRoutes) {
    return null;
  }

  return (
    <Border
      style={{ '--rarity-color': getRarityRGBColor(rarity) } as CSSProperties}
    >
      <Root>
        <TitleBlock>
          <LeftContent>
            <FormattedMessage
              id="MarketRoutesBlock.title"
              defaultMessage="<yellow>Buy players</yellow>{br}your way"
              values={{
                yellow: chunks => <span style={{ color }}>{chunks}</span>,
                br: <br />,
              }}
            />
          </LeftContent>
        </TitleBlock>
        <Block className="border" to={auctionMarketUrl}>
          <Auction size={2} color={color} />
          <Vertical gap={0.5}>
            <SBHorizontal>
              <BrandT>
                <FormattedMessage {...navLabels.auctions} />
              </BrandT>
              <ChevronRightBold />
            </SBHorizontal>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                id="MarketRoutesBlock.auctions.desc"
                defaultMessage="Bid & win at the best price from Sorare"
              />
            </BodyS>
          </Vertical>
        </Block>
        <Block className="border" to={instantBuyUrl}>
          <InstantBuy size={2} color={color} />
          <Vertical gap={0.5}>
            <SBHorizontal>
              <BrandT>
                <FormattedMessage {...transferMarket.instantBuy} />
              </BrandT>
              <ChevronRightBold />
            </SBHorizontal>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                id="MarketRoutesBlock.instantBuy.desc"
                defaultMessage="Buy instantly from Sorare"
              />
            </BodyS>
          </Vertical>
        </Block>
        <Block to={secondaryMarketUrl}>
          <Transfer size={2} color={color} />
          <Vertical gap={0.5}>
            <SBHorizontal>
              <BrandT>
                <FormattedMessage {...transferMarket.transfer} />
              </BrandT>
              <ChevronRightBold />
            </SBHorizontal>

            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                id="MarketRoutesBlock.secondaryMarket.desc"
                defaultMessage="Deals from other Managers"
              />
            </BodyS>
          </Vertical>
        </Block>
      </Root>
    </Border>
  );
};
